import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { api } from '@/api/client';
import type { User, ApiResponse } from '@/api/types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Returns the fetched User so callers (e.g. magic-link Verify) can
   *  branch on `onboardingCompleted` without re-reading context state. */
  login: (token: string) => Promise<User | null>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async (): Promise<User | null> => {
    try {
      const response = await api.get<ApiResponse<User>>('/auth/me');
      setUser(response.data);
      return response.data;
    } catch {
      // Token invalid or expired
      api.setToken(null);
      setUser(null);
      return null;
    }
  }, []);

  const login = useCallback(async (token: string): Promise<User | null> => {
    api.setToken(token);
    return fetchUser();
  }, [fetchUser]);

  const logout = useCallback(() => {
    api.setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (api.getToken()) {
      await fetchUser();
    }
  }, [fetchUser]);

  // Check for existing token on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = api.getToken();
      if (token) {
        await fetchUser();
      }
      setIsLoading(false);
    };

    initAuth();
  }, [fetchUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Co-located with the provider because both hooks read the same context.
// Splitting into separate files just to satisfy react-refresh adds files
// without payoff — disable the warning here.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// ═══════════════════════════════════════════════════════════════════
// useRole — derived view over AuthContext for admin-gated UI.
//
// Backend already enforces authorization on /admin/* endpoints via the
// `requireAdmin` middleware. This hook just decides whether to *show*
// admin entry points (cards on the dashboard, the Admin sub-nav). Any
// admin route a non-admin reaches directly will 403 from the API — the
// frontend gate is convenience, not security.
// ═══════════════════════════════════════════════════════════════════

export interface RoleState {
  /** Raw role string from the backend (`admin`, `super_admin`, `user`,
   *  or undefined when unauthenticated). */
  role: User['role'] | undefined;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRole(): RoleState {
  const { user } = useAuth();
  const role = user?.role;
  return {
    role,
    isAdmin: role === 'admin' || role === 'super_admin',
    isSuperAdmin: role === 'super_admin',
  };
}
